// Core loader excludes session-replay/rrweb (not used in Phase 1).
import mixpanel from "mixpanel-browser/src/loaders/loader-module-core"
import type { AnalyticsEventName, AnalyticsEventProps, AnalyticsGlobalProps } from "@/lib/analytics-events"

const MIXPANEL_TOKEN =
  typeof import.meta !== "undefined" && typeof import.meta.env?.VITE_MIXPANEL_TOKEN === "string"
    ? import.meta.env.VITE_MIXPANEL_TOKEN.trim()
    : ""

const RELEASE_VERSION =
  typeof import.meta !== "undefined" && typeof import.meta.env?.VITE_RELEASE_VERSION === "string"
    ? import.meta.env.VITE_RELEASE_VERSION.trim()
    : undefined

const SENSITIVE_KEY_PATTERN = /(password|secret|token|api[-_]?key|authorization|cookie|email|phone|address|notes?)/i
const SESSION_ID_KEY = "analytics_session_id"
const MAX_STRING_LENGTH = 240

let initialized = false

function getEnvironmentName(): string {
  if (typeof import.meta === "undefined") return "unknown"
  if (import.meta.env?.PROD) return "production"
  return import.meta.env?.MODE || "development"
}

function inferPageFromPath(pathname: string): string {
  if (pathname === "/") return "home"
  if (pathname.startsWith("/search")) return "search"
  if (pathname.startsWith("/business/")) return "business_detail"
  if (pathname.startsWith("/about")) return "about"
  if (pathname.startsWith("/admin")) return "admin"
  if (pathname.startsWith("/auth/request-link")) return "auth_request_link"
  if (pathname.startsWith("/auth/verify")) return "auth_verify"
  if (pathname.startsWith("/dashboard/my-business")) return "my_business"
  return "other"
}

function getOrCreateSessionId(): string | undefined {
  if (typeof window === "undefined") return undefined

  const existing = window.sessionStorage.getItem(SESSION_ID_KEY)
  if (existing) return existing

  const sessionId =
    typeof crypto !== "undefined" && typeof crypto.randomUUID === "function"
      ? crypto.randomUUID()
      : (() => {
          const bytes = new Uint8Array(16)
          crypto.getRandomValues(bytes)
          return Array.from(bytes, (byte) => byte.toString(16).padStart(2, "0")).join("")
        })()
  window.sessionStorage.setItem(SESSION_ID_KEY, sessionId)
  return sessionId
}

function sanitizeUrlOriginPath(urlValue: string | undefined): string | undefined {
  if (!urlValue) return undefined

  try {
    const parsedUrl = new URL(urlValue)
    return `${parsedUrl.origin}${parsedUrl.pathname}`
  } catch {
    return undefined
  }
}

function sanitizeProperty(key: string, value: unknown): string | number | boolean | null | undefined {
  if (value === undefined) return undefined
  if (value === null) return null

  if (SENSITIVE_KEY_PATTERN.test(key)) {
    return undefined
  }

  if (typeof value === "string") {
    if (key === "query_text") return undefined
    if (key === "source_url" || key === "referrer") {
      const sanitizedUrl = sanitizeUrlOriginPath(value)
      return sanitizedUrl ? sanitizedUrl.slice(0, MAX_STRING_LENGTH) : undefined
    }
    return value.slice(0, MAX_STRING_LENGTH)
  }

  if (typeof value === "number" || typeof value === "boolean") {
    return value
  }

  return undefined
}

function sanitizeProperties(properties: Record<string, unknown>): Record<string, string | number | boolean | null> {
  const sanitized: Record<string, string | number | boolean | null> = {}

  Object.entries(properties).forEach(([key, value]) => {
    const sanitizedValue = sanitizeProperty(key, value)
    if (sanitizedValue !== undefined) {
      sanitized[key] = sanitizedValue
    }
  })

  return sanitized
}

function buildGlobalProps(override?: Partial<AnalyticsGlobalProps>): AnalyticsGlobalProps {
  const route =
    override?.route ||
    (typeof window !== "undefined" && window.location?.pathname ? window.location.pathname : "")

  return {
    app: "impact_network",
    platform: "web",
    environment: getEnvironmentName(),
    route,
    page: override?.page || inferPageFromPath(route),
    surface: override?.surface,
    session_id: override?.session_id || getOrCreateSessionId(),
    release_version: override?.release_version || RELEASE_VERSION,
  }
}

export function initAnalytics(): void {
  if (initialized || !MIXPANEL_TOKEN || typeof window === "undefined") return

  try {
    mixpanel.init(MIXPANEL_TOKEN, {
      persistence: "localStorage",
      autocapture: false,
      track_pageview: false,
    })
    initialized = true
  } catch (error) {
    console.warn("Analytics init failed", {
      error: error instanceof Error ? error.message : "unknown",
    })
  }
}

export function analyticsEnabled(): boolean {
  return Boolean(MIXPANEL_TOKEN) && typeof window !== "undefined"
}

export function trackEvent<T extends AnalyticsEventName>(
  eventName: T,
  properties: AnalyticsEventProps<T>,
  globalOverride?: Partial<AnalyticsGlobalProps>,
): void {
  if (!analyticsEnabled()) return
  try {
    if (!initialized) initAnalytics()
    if (!initialized) return

    const payload = {
      ...buildGlobalProps(globalOverride),
      ...properties,
    }

    mixpanel.track(eventName, sanitizeProperties(payload))
  } catch (error) {
    console.warn("Analytics trackEvent failed", {
      eventName,
      error: error instanceof Error ? error.message : "unknown",
    })
  }
}

export function trackPageView(source = "route_change"): void {
  const title =
    typeof document !== "undefined" && typeof document.title === "string"
      ? document.title
      : undefined
  const referrer =
    typeof document !== "undefined" && typeof document.referrer === "string"
      ? sanitizeUrlOriginPath(document.referrer)
      : undefined

  trackEvent("page_view", {
    source,
    title,
    referrer,
  })
}
